/**
 * USASpending React Query Hooks
 *
 * Custom hooks for fetching USASpending data using react-query.
 */

export { usaspendingKeys, appDbKeys, type ListParams } from './queryKeys'
export {
  useAward,
  useAwardTransactions,
  useAwardSubawards,
  useAwardOrders,
  useIdvChildAggregation,
  useIdvActivity,
} from './useAward'
export { useAwardExistenceCheck } from './useAwardExistenceCheck'
export {
  useAgencyExistenceCheck,
  useRecipientExistenceCheck,
} from './useExistence'
export { useRecipient } from './useRecipient'
export { useAgency } from './useAgency'
export { usePerson } from './usePerson'

// Query Options Factories (TanStack Query v5 pattern)
export {
  useAwardQueries,
  useRecipientQueries,
  useAgencyQueries,
  useAgencySectionQueries,
  useRecipientSectionQueries,
  usePersonQueries,
  useSubawardQueries,
} from './queryOptions'

// Suspense-compatible hooks
export {
  useSuspenseAward,
  useSuspenseAwardTransactions,
  useSuspenseAwardSubawards,
  useSuspenseRecipient,
  useSuspenseAgency,
  useSuspensePerson,
} from './useSuspense'

// Smart skeleton fallback hooks (read cache without fetching)
export {
  useSkeletonAwardData,
  useSkeletonRecipientData,
  useSkeletonAgencyData,
  useSkeletonPersonData,
} from './useSkeletonData'

// List hooks - USASpending DB
export {
  useRecipientAwardList,
  fetchRecipientAwards,
  type AwardListItem,
  type ListResponse,
} from './useLists'

// List hooks - Application DB
export {
  useFederalContractList,
  useFederalGrantList,
  useDibbsList,
  useSbirList,
  useSledContractList,
  type FederalContractListItem,
  type FederalGrantListItem,
  type DibbsContractListItem,
  type SbirListItem,
  type SledContractListItem,
} from './useLists'

// Analytics hooks - Recipient Dashboard
export {
  recipientAnalyticsKeys,
  useRecipientAnalyticsQueries,
  useRecipientTimeSeries,
  useRecipientAwardTypes,
  useRecipientGeography,
  useRecipientGeographyCities,
  useRecipientSetAsides,
  useRecipientCompetition,
  useRecipientContractSizes,
  useSuspenseRecipientTimeSeries,
  useSuspenseRecipientAwardTypes,
  useSuspenseRecipientGeography,
  useSuspenseRecipientSetAsides,
  useSuspenseRecipientCompetition,
  useSuspenseRecipientContractSizes,
  useSuspenseRecipientDashboard,
  useRecipientAwardPortfolio,
  useRecipientOutlayTimeSeries,
  useRecipientCeilingTimeSeries,
  useIdvGroupTimeSeries,
  useAwardTimeSeries,
  useIdvParent,
} from './useRecipientAnalytics'

// Aggregate cash flow curves hook (shared by compact + expanded chart views)
export { useAggregateCashFlowCurves } from './useAggregateCashFlowCurves'

// Analytics hooks - Agency Dashboard
export {
  agencyAnalyticsKeys,
  useAgencyAnalyticsQueries,
  useAgencyTimeSeries,
  useAgencyAwardTypes,
  useAgencyGeography,
  useAgencySetAsides,
  useAgencyTopPscCodes,
  useSuspenseAgencyTimeSeries,
  useSuspenseAgencyAwardTypes,
  useSuspenseAgencyGeography,
  useSuspenseAgencySetAsides,
  useSuspenseAgencyTopPscCodes,
} from './useAgencyAnalytics'

// PDF Export hook
export {
  useRecipientPdfExport,
  type ChartRefRegistry,
  type UseRecipientPdfExportOptions,
  type UseRecipientPdfExportReturn,
} from './useRecipientPdfExport'
